import { SwitchWithLabel } from '@newfront-insurance/core-ui';
import { useState } from 'react';

import { useChatOverlayContext } from '../contexts/chat-overlay-context';

/**
 * Notification settings component that displays a toggle switch for enabling/disabling browser notifications.
 * Uses the notificationController from context to manage notification state.
 */
export function NotificationSettings() {
  const { notificationController } = useChatOverlayContext();
  const [isLoading, setIsLoading] = useState(false);

  const {
    browserNotificationsEnabled,
    browserNotificationPermission,
    browserNotificationsSupported,
    enableBrowserNotifications,
    disableBrowserNotifications,
  } = notificationController;

  const handleToggle = async () => {
    if (!browserNotificationsSupported) {
      return;
    }

    setIsLoading(true);
    try {
      if (browserNotificationsEnabled) {
        disableBrowserNotifications();
      } else {
        const success = await enableBrowserNotifications();

        if (!success && browserNotificationPermission === 'denied') {
          // eslint-disable-next-line no-console
          console.warn('[Nova Notifications] Notifications are blocked. Please enable them in your browser settings.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if browser doesn't support notifications
  if (!browserNotificationsSupported) {
    return null;
  }

  const isDisabled = isLoading || browserNotificationPermission === 'denied';

  return (
    <div className="flex flex-col">
      {/* First row: Notifications label with toggle */}
      <div className="flex items-center justify-between px-0 py-2">
        <h3 className="font-medium">Notifications</h3>
        <SwitchWithLabel
          label=""
          enabled={browserNotificationsEnabled}
          onClick={handleToggle}
          locked={isDisabled}
          labelPosition="left"
        />
      </div>

      {/* Divider */}
      <div className="border-border w-full border-t" />

      {/* Second row: Description */}
      <div className="px-0 py-2">
        <p className="text-muted-foreground text-sm">Get notified when your chat conversations receive new messages.</p>
        {browserNotificationPermission === 'denied' && (
          <p className="text-destructive mt-2 text-sm">
            Notifications are blocked by your browser. Please enable them in your browser settings to receive
            notifications.
          </p>
        )}
      </div>
    </div>
  );
}
